import React, { useState, useEffect, useCallback, useRef } from "react"
import { Users, Plus, Upload, Search, ChevronDown } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { useToast } from "src/components/ui/toast"

import { useLeads, useDeleteLead, useLeadCounts } from "src/features/leads/hooks/useLeads"
import { useLeadStatuses } from "src/features/leads/hooks/useLeadStatuses"
import { useDuplicateClusters, useMergeLeads } from "src/features/leads/hooks/useDuplicates"
import { LeadsTable } from "src/features/leads/components/LeadsTable"
import { LeadScopeTabs } from "src/features/leads/components/LeadScopeTabs"
import { LeadDrawer } from "src/features/leads/components/LeadDrawer"
import { SendMessageDrawer } from "src/features/leads/components/SendMessageDrawer"
import { DeleteLeadModal } from "src/features/leads/components/DeleteLeadModal"
import { ImportLeadsModal } from "src/features/leads/components/ImportLeadsModal"
import { DuplicateClusters } from "src/features/leads/components/DuplicateClusters"
import { MergeLeadsDialog } from "src/features/leads/components/MergeLeadsDialog"

const SOURCES = ["channel_partner", "direct", "referral", "campaign", "walk_in", "qr_campaign"]
const SOURCE_LABELS = {
  channel_partner: "Channel Partner", direct: "Direct",
  referral: "Referral", campaign: "Campaign", walk_in: "Walk-in",
  qr_campaign: "QR Campaign",
}

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function LeadsPage() {
  const { toast } = useToast()
  const { statuses: leadStatuses } = useLeadStatuses()

  // ── Filters + pagination state ─────────────────────────────────────────────
  const [page, setPage] = useState(1)
  const [scope, setScope] = useState("all")
  const [searchRaw, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const [source, setSource] = useState("")
  const q = useDebounce(searchRaw)

  // ── Drawer / modal state ───────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingLead, setEditingLead] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [importOpen, setImportOpen] = useState(false)
  const [messageTarget, setMessageTarget] = useState(null) // { lead, channel }

  // ── Data ───────────────────────────────────────────────────────────────────
  const { data, loading, fetch } = useLeads()
  const { counts, refresh: refreshCounts } = useLeadCounts()
  const deleteHook = useDeleteLead()

  // ── Duplicate review / merge ───────────────────────────────────────────────
  const isDuplicateView = scope === "duplicate"
  const { clusters, loading: clustersLoading, refresh: refreshClusters } = useDuplicateClusters()
  const mergeHook = useMergeLeads()
  const [mergeCluster, setMergeCluster] = useState(null)

  const currentFilters = useCallback(
    () => ({ page, per_page: 20, scope, q, status, source }),
    [page, scope, q, status, source]
  )

  useEffect(() => {
    if (isDuplicateView) refreshClusters()
    else fetch(currentFilters())
  }, [page, scope, q, status, source])

  // Counts are independent of the active scope/filters — load once on mount.
  useEffect(() => { refreshCounts() }, [refreshCounts])

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1) }, [scope, q, status, source])

  // ── Handlers ───────────────────────────────────────────────────────────────
  function openCreate() { setEditingLead(null); setDrawerOpen(true) }
  function openEdit(lead) { setEditingLead(lead); setDrawerOpen(true) }
  function openDelete(lead) { setDeleteTarget(lead) }
  function openMessage(lead, channel) { setMessageTarget({ lead, channel }) }

  async function handleDelete(id) {
    try {
      await deleteHook.mutate(id)
      toast({ title: "Lead deleted" })
      setDeleteTarget(null)
      fetch(currentFilters())
      refreshCounts()
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to delete lead" })
    }
  }

  function handleDrawerSuccess() { fetch(currentFilters()); refreshCounts() }
  function handleImportSuccess() { fetch(currentFilters()); refreshCounts() }

  async function handleMerge({ primaryId, duplicateIds }) {
    try {
      await mergeHook.mutate({ primaryId, duplicateIds })
      toast({ title: "Leads merged", description: `${duplicateIds.length} duplicate${duplicateIds.length !== 1 ? "s" : ""} folded into the primary.` })
      setMergeCluster(null)
      mergeHook.reset()
      refreshClusters()
      refreshCounts()
    } catch {
      // Error surfaced inline in the dialog via mergeHook.error.
    }
  }

  const leads = data?.leads ?? []
  const meta = data?.meta

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Users className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Leads</h1>
              <p className="text-[13px] text-slate-500">
                {meta ? `${meta.total} total lead${meta.total !== 1 ? "s" : ""}` : "Manage and track your real estate leads"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4 text-slate-500" />
              Import
            </Button>
            <Button variant="default" size="sm" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New Lead
            </Button>
          </div>
        </div>

        {/* ── Scope Tabs ── */}
        <LeadScopeTabs value={scope} onChange={setScope} counts={counts} />

        {/* ── Filter Bar (hidden in the grouped duplicate view) ── */}
        {!isDuplicateView && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search
              style={{ left: "0.75rem" }}
              className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              placeholder="Search name, phone, email…"
              value={searchRaw}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-40 justify-between font-normal text-slate-700">
                {status ? (leadStatuses.find((s) => s.value === status)?.label || status) : "All Statuses"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-40">
              <DropdownMenuRadioGroup value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                <DropdownMenuRadioItem value="all">All Statuses</DropdownMenuRadioItem>
                {leadStatuses.map((s) => (
                  <DropdownMenuRadioItem key={s.value} value={s.value}>
                    {s.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-44 justify-between font-normal text-slate-700">
                {source ? SOURCE_LABELS[source] : "All Sources"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-44">
              <DropdownMenuRadioGroup value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
                <DropdownMenuRadioItem value="all">All Sources</DropdownMenuRadioItem>
                {SOURCES.map((s) => (
                  <DropdownMenuRadioItem key={s} value={s}>
                    {SOURCE_LABELS[s]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        )}

        {/* ── Body: grouped duplicate clusters or the standard table ── */}
        {isDuplicateView ? (
          <DuplicateClusters
            clusters={clusters}
            loading={clustersLoading}
            onMerge={setMergeCluster}
          />
        ) : (
          <LeadsTable
            leads={leads}
            loading={loading}
            meta={meta}
            filters={{ q, status, source, scope }}
            page={page}
            onPageChange={setPage}
            onEdit={openEdit}
            onDelete={openDelete}
            onNewLead={openCreate}
            onMessage={openMessage}
          />
        )}
      </div>

      {/* ── Overlays ── */}
      <LeadDrawer
        open={drawerOpen}
        lead={editingLead}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleDrawerSuccess}
      />

      <DeleteLeadModal
        open={!!deleteTarget}
        lead={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={deleteHook.isPending}
      />

      <ImportLeadsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={handleImportSuccess}
      />

      <MergeLeadsDialog
        open={!!mergeCluster}
        cluster={mergeCluster}
        onClose={() => { setMergeCluster(null); mergeHook.reset() }}
        onConfirm={handleMerge}
        isMerging={mergeHook.isPending}
        error={mergeHook.error}
      />

      <SendMessageDrawer
        open={!!messageTarget}
        lead={messageTarget?.lead}
        channel={messageTarget?.channel}
        onClose={() => setMessageTarget(null)}
      />
    </AppShell>
  )
}
